import React, { useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, OrbitControls, Stars } from "@react-three/drei";

function useHasWebGL() {
  const [hasWebGL, setHasWebGL] = useState(true);
  useEffect(() => {
    try {
      const canvas = document.createElement("canvas");
      const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
      setHasWebGL(!!gl);
    } catch (e) {
      setHasWebGL(false);
    }
  }, []);
  return hasWebGL;
}

// 3D floating object representing garment structure
function FloatingGarmentShape({ shapeType = "torusKnot", color = "#4F46E5" }) {
  const meshRef = useRef();
  const [hovered, setHover] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      // Gentle floating rotation
      meshRef.current.rotation.x += 0.005;
      meshRef.current.rotation.y += 0.008;
      
      // Mouse movement interactive tilt
      const targetX = (state.pointer.x * Math.PI) / 6;
      const targetY = (state.pointer.y * Math.PI) / 6;
      meshRef.current.rotation.x += (targetY - meshRef.current.rotation.x) * 0.1;
      meshRef.current.rotation.y += (targetX - meshRef.current.rotation.y) * 0.1;
    }
  });

  const getGeometry = () => {
    switch (shapeType) {
      case "cube":
        return <boxGeometry args={[1.5, 1.5, 1.5]} />;
      case "sphere":
        return <sphereGeometry args={[1.1, 32, 32]} />;
      case "torus":
        return <torusGeometry args={[0.9, 0.4, 16, 100]} />;
      case "torusKnot":
        return <torusKnotGeometry args={[0.8, 0.25, 150, 16]} />;
      case "dodecahedron":
        return <dodecahedronGeometry args={[1.1]} />;
      default:
        return <torusKnotGeometry args={[0.8, 0.25, 150, 16]} />;
    }
  };

  return (
    <mesh
      ref={meshRef}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}
      scale={hovered ? 1.15 : 1.0}
    >
      {getGeometry()}
      <meshStandardMaterial
        color={color}
        wireframe={true}
        roughness={0.1}
        metalness={0.9}
        emissive={color}
        emissiveIntensity={hovered ? 0.6 : 0.25}
      />
    </mesh>
  );
}

// Particle field in 3D
function ParticleField() {
  return (
    <Stars
      radius={100}
      depth={50}
      count={1500}
      factor={4}
      saturation={0}
      fade
      speed={1.5}
    />
  );
}

export default function ThreeCanvas({ shapeType = "torusKnot", color = "#4F46E5", isInteractive = false }) {
  const hasWebGL = useHasWebGL();

  if (!hasWebGL) {
    // Elegant CSS 3D fallback for headless browsers or non-WebGL systems
    return (
      <div 
        data-testid="3d-fallback-element"
        className="w-full h-full flex items-center justify-center relative overflow-hidden"
        style={{ background: "radial-gradient(circle, rgba(79,70,229,0.15) 0%, rgba(244,244,242,0) 70%)" }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="w-48 h-48 border-2 border-dashed opacity-40 animate-spin" 
            style={{ borderColor: color, animationDuration: "12s" }}
          />
          <div 
            className="w-36 h-36 border border-double absolute opacity-60 animate-reverse-spin" 
            style={{ borderColor: color, animationDuration: "8s" }}
          />
          <div 
            className="w-16 h-16 rounded-full absolute animate-pulse-accent"
            style={{ backgroundColor: color }}
          />
        </div>
        <style>{`
          @keyframes reverse-spin {
            from { transform: rotate(360deg); }
            to { transform: rotate(0deg); }
          }
          .animate-reverse-spin {
            animation: reverse-spin infinite linear;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative" data-testid="3d-canvas-container">
      <Canvas camera={{ position: [0, 0, 4.5], fov: 45 }}>
        <ambientLight intensity={0.6} />
        <pointLight position={[10, 10, 10]} intensity={1.5} />
        <directionalLight position={[-5, 5, 5]} intensity={1.0} />
        
        <Float speed={2.5} rotationIntensity={0.6} floatIntensity={1.2}>
          <FloatingGarmentShape shapeType={shapeType} color={color} />
        </Float>
        
        <ParticleField />
        
        {isInteractive && (
          <OrbitControls 
            enableZoom={true} 
            enablePan={false} 
            minDistance={3} 
            maxDistance={8} 
          />
        )}
      </Canvas>
    </div>
  );
}
